import React, { useEffect, useRef, useState } from 'react';
import { DialogRequest, settleDialog, subscribeDialogs } from '../utils/dialog';
import { lockBodyScroll } from '../utils/scrollLock';

/**
 * 全局对话框宿主 (2026-09-12): 渲染 utils/dialog 的队列, 替代原生 alert/confirm/prompt.
 * 样式对齐 Modal (遮罩 + 居中卡片); ESC = 取消, Enter = 确定 (prompt 在输入框内回车).
 */
const DialogHost = () => {
    const [queue, setQueue] = useState<DialogRequest[]>([]);
    const [input, setInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const cur = queue[0];

    useEffect(() => subscribeDialogs(setQueue), []);
    useEffect(() => {
        if (!cur) return;
        setInput(cur.defaultValue || '');
        const t = window.setTimeout(() => inputRef.current?.focus(), 30);
        return () => window.clearTimeout(t);
    }, [cur?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!cur) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') cancel();
            else if (e.key === 'Enter' && cur.kind !== 'prompt') ok();
        };
        document.addEventListener('keydown', onKey);
        const unlock = lockBodyScroll();
        return () => {
            document.removeEventListener('keydown', onKey);
            unlock();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cur?.id, input]);

    if (!cur) return null;
    const ok = () => settleDialog(cur.id, cur.kind === 'prompt' ? input : true);
    const cancel = () => settleDialog(cur.id, cur.kind === 'prompt' ? null : false);
    const title = cur.title || (cur.kind === 'alert' ? '提示' : cur.kind === 'confirm' ? '请确认' : '请输入');
    const okCls = cur.danger
        ? 'px-4 py-1.5 rounded-md text-sm text-white bg-rose-600 hover:bg-rose-700'
        : 'btn-primary';

    return (
        <div
            className="fixed inset-0 !m-0 z-[60] flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,.45)' }}
            onClick={cur.kind === 'alert' ? ok : cancel}
        >
            <div
                className="bg-white rounded-xl shadow-xl w-[440px] max-w-full"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="px-5 pt-4 pb-2 text-base font-semibold text-slate-800">{title}</div>
                <div className="px-5 pb-4 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                    {cur.message}
                    {cur.kind === 'prompt' && (
                        <input
                            ref={inputRef}
                            className="inp w-full mt-3"
                            value={input}
                            placeholder={cur.placeholder}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') ok();
                            }}
                        />
                    )}
                </div>
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
                    {cur.kind !== 'alert' && (
                        <button type="button" className="btn-ghost" onClick={cancel}>
                            {cur.cancelText || '取消'}
                        </button>
                    )}
                    <button type="button" className={okCls} onClick={ok} autoFocus={cur.kind !== 'prompt'}>
                        {cur.okText || (cur.kind === 'alert' ? '知道了' : '确定')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DialogHost;
